import { cn } from "@/shared/lib/utils"
import type { WeatherDataResponse } from "@/shared/types/api"

interface WeatherDisplayProps {
  weather: WeatherDataResponse | null | undefined
  className?: string
  compact?: boolean
}

// Wind direction to compass direction
function getWindDirection(degrees: number): string {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
  const index = Math.round(degrees / 22.5) % 16
  return directions[index]
}

// Format the recorded time
function formatRecordedTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function WeatherDisplay({ weather, className, compact = false }: WeatherDisplayProps) {
  if (!weather) {
    return (
      <div className={cn("bg-slate-50/50 rounded-xl p-4 border border-slate-200/50", className)}>
        <div className="flex items-center gap-2 text-slate-400">
          <CloudIcon className="h-5 w-5" />
          <span className="text-sm italic">No weather data available</span>
        </div>
      </div>
    )
  }

  if (compact) {
    return (
      <div className={cn("bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-4 border border-sky-200/50", className)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-100 rounded-lg">
              <WeatherIcon code={weather.weatherCode} className="h-6 w-6 text-sky-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">
                {weather.temperatureCelsius != null ? `${weather.temperatureCelsius.toFixed(1)}°C` : '--'}
              </p>
              <p className="text-xs text-slate-500">{weather.weatherDescription || 'Unknown'}</p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p>{formatRecordedTime(weather.recordedAt)}</p>
            <p className="text-slate-400">{weather.source}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl border border-sky-200/50 overflow-hidden", className)}>
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-sky-100/50 to-blue-100/50 border-b border-sky-200/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CloudIcon className="h-4 w-4 text-sky-600" />
            <h4 className="text-sm font-medium text-slate-700">Weather Conditions</h4>
          </div>
          <span className="text-xs text-slate-500">{formatRecordedTime(weather.recordedAt)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Main weather info */}
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-white/80 rounded-xl shadow-sm">
            <WeatherIcon code={weather.weatherCode} className="h-10 w-10 text-sky-600" />
          </div>
          <div>
            <p className="text-3xl font-bold text-slate-900">
              {weather.temperatureCelsius != null ? `${weather.temperatureCelsius.toFixed(1)}°C` : '--'}
            </p>
            <p className="text-sm text-slate-600">{weather.weatherDescription || 'Unknown conditions'}</p>
          </div>
        </div>

        {/* Weather details grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {weather.humidityPercent != null && (
            <WeatherMetric
              icon={<DropletIcon className="h-4 w-4" />}
              label="Humidity"
              value={`${weather.humidityPercent.toFixed(0)}%`}
            />
          )}
          {weather.windSpeedKmh != null && (
            <WeatherMetric
              icon={<WindIcon className="h-4 w-4" />}
              label="Wind"
              value={`${weather.windSpeedKmh.toFixed(0)} km/h`}
              subvalue={weather.windDirectionDegrees != null ? getWindDirection(weather.windDirectionDegrees) : undefined}
            />
          )}
          {weather.precipitationMm != null && (
            <WeatherMetric
              icon={<RainIcon className="h-4 w-4" />}
              label="Precipitation"
              value={`${weather.precipitationMm.toFixed(1)} mm`}
            />
          )}
          {weather.pressureHpa != null && (
            <WeatherMetric
              icon={<GaugeIcon className="h-4 w-4" />}
              label="Pressure"
              value={`${weather.pressureHpa.toFixed(0)} hPa`}
            />
          )}
          {weather.cloudCoverPercent != null && (
            <WeatherMetric
              icon={<CloudIcon className="h-4 w-4" />}
              label="Cloud Cover"
              value={`${weather.cloudCoverPercent.toFixed(0)}%`}
            />
          )}
          {weather.uvIndex != null && (
            <WeatherMetric
              icon={<SunIcon className="h-4 w-4" />}
              label="UV Index"
              value={weather.uvIndex.toFixed(1)}
              subvalue={getUVLevel(weather.uvIndex)}
            />
          )}
        </div>

        {/* Source info */}
        <div className="mt-4 pt-3 border-t border-sky-200/50">
          <p className="text-xs text-slate-400">
            Source: {weather.source} {weather.sourceModel && `(${weather.sourceModel})`}
          </p>
        </div>
      </div>
    </div>
  )
}

interface WeatherMetricProps {
  icon: React.ReactNode
  label: string
  value: string
  subvalue?: string
}

function WeatherMetric({ icon, label, value, subvalue }: WeatherMetricProps) {
  return (
    <div className="bg-white/60 rounded-lg p-2.5">
      <div className="flex items-center gap-1.5 text-slate-500 mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className="font-semibold text-slate-900 text-sm">{value}</p>
      {subvalue && <p className="text-xs text-slate-500">{subvalue}</p>}
    </div>
  )
}

function getUVLevel(uvIndex: number): string {
  if (uvIndex < 3) return 'Low'
  if (uvIndex < 6) return 'Moderate'
  if (uvIndex < 8) return 'High'
  if (uvIndex < 11) return 'Very High'
  return 'Extreme'
}

// Weather icons based on WMO codes
interface IconProps {
  className?: string
}

function WeatherIcon({ code, className }: { code?: number; className?: string }) {
  if (code == null) return <CloudIcon className={className} />

  // WMO weather codes
  if (code === 0) return <SunIcon className={className} /> // Clear sky
  if (code <= 3) return <PartlyCloudyIcon className={className} /> // Partly cloudy
  if (code <= 48) return <FogIcon className={className} /> // Fog
  if (code <= 57) return <DrizzleIcon className={className} /> // Drizzle
  if (code <= 67) return <RainIcon className={className} /> // Rain
  if (code <= 77) return <SnowIcon className={className} /> // Snow
  if (code <= 86) return <ShowerIcon className={className} /> // Showers
  if (code >= 95) return <ThunderstormIcon className={className} /> // Thunderstorm

  return <CloudIcon className={className} />
}

// Simple SVG icons
function SunIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  )
}

function CloudIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
    </svg>
  )
}

function PartlyCloudyIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3v1M18.36 5.64l-.71.71M21 12h-1M18.36 18.36l-.71-.71M12 21v-1M5.64 18.36l.71-.71M3 12h1M5.64 5.64l.71.71" />
      <circle cx="12" cy="12" r="4" />
      <path d="M16 14h1a4 4 0 0 1 0 8H8a5 5 0 0 1-1-9.9" />
    </svg>
  )
}

function FogIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 14h16M4 18h16M8 10h8M6 6h12" />
    </svg>
  )
}

function DrizzleIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 13v2M8 13v2M12 15v2M16 17v2M8 17v2M12 19v2" />
      <path d="M17 10a5 5 0 0 0-9.7-1.4A4 4 0 0 0 8 17h9a4 4 0 0 0 0-7z" />
    </svg>
  )
}

function RainIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 13v8M8 13v8M12 15v8" />
      <path d="M17 10a5 5 0 0 0-9.7-1.4A4 4 0 0 0 8 17h9a4 4 0 0 0 0-7z" />
    </svg>
  )
}

function SnowIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2v20M17 7l-10 10M7 7l10 10" />
    </svg>
  )
}

function ShowerIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 10a5 5 0 0 0-9.7-1.4A4 4 0 0 0 8 17h9a4 4 0 0 0 0-7z" />
      <path d="M8 19v2M8 13v2M12 21v2M12 15v2M16 19v2M16 13v2" />
    </svg>
  )
}

function ThunderstormIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 10a5 5 0 0 0-9.7-1.4A4 4 0 0 0 8 17h9a4 4 0 0 0 0-7z" />
      <path d="M13 12l-3 5h4l-3 5" />
    </svg>
  )
}

function DropletIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  )
}

function WindIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
    </svg>
  )
}

function GaugeIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  )
}
